import { gql, useMutation, useQuery } from '@apollo/client';
import { Loading } from '@everlutionsk/ui';
import { useFlashMutation } from '@everlutionsk/ui-apollo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  alpha,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  SxProps,
  TextField,
  Theme,
  Typography
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { Button } from '../components/coreComponents/button';
import { ChipListField } from '../components/coreComponents/chipListField';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { AdminSlackAction } from '../graphql/types';
import { useIsSuperAdmin } from '../identityContext';

// Action key → human label + one-line description shown on each routing card.
// Keys match the AdminSlackAction enum in the admin schema.
const ACTION_META: Record<
  AdminSlackAction,
  { readonly label: string; readonly description: string }
> = {
  accountProvisioned: {
    label: 'Account provisioned',
    description: 'A new tenant account finished provisioning.'
  },
  accountPublished: {
    label: 'Account published',
    description: 'An account was published and owner credentials were issued.'
  },
  accountDeleteConfirmed: {
    label: 'Account deleted',
    description: 'A soft-delete job completed. The account is recoverable for 180 days.'
  },
  accountRestored: {
    label: 'Account restored',
    description: 'A previously deleted account was restored from backup.'
  },
  accountBackupPurged: {
    label: 'Account backup purged',
    description: 'A backup was permanently purged — the account is now unrecoverable.'
  },
  pendingActionRequested: {
    label: 'Pending action requested',
    description:
      'A two-person action was raised — soft-delete, backup purge, or admin credential grant — awaiting confirmation.'
  },
  adminCredentialsGranted: {
    label: 'Admin credentials granted',
    description: 'An admin credential grant was confirmed — the administrator can now sign in.'
  },
  adminCredentialsRevoked: {
    label: 'Admin access revoked',
    description:
      'An admin credential revoke was confirmed — the administrator can no longer sign in.'
  }
};

interface Rule {
  readonly action: AdminSlackAction;
  readonly enabled: boolean;
  readonly channels: readonly string[];
  readonly recipients: readonly string[];
}

interface Settings {
  readonly enabled: boolean;
  readonly defaultChannel?: string | null;
  readonly workspaceName?: string | null;
  readonly hasBotToken: boolean;
  readonly rules: readonly Rule[];
}

export function Notifications() {
  const isSuperAdmin = useIsSuperAdmin();
  const { data, loading } = useQuery(adminSlackSettingsQuery, { fetchPolicy: 'cache-and-network' });

  if (!isSuperAdmin) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Slack notifications" />
        <Alert severity="warning">
          You need superAdmin privileges to manage Slack notifications.
        </Alert>
      </MainWrapper>
    );
  }

  if (loading && data == null) return <Loading />;

  const settings: Settings | undefined = data?.adminSlackSettings;
  if (settings == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Slack notifications" />
        <Alert severity="error">Failed to load Slack notification settings.</Alert>
      </MainWrapper>
    );
  }

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Slack notifications"
        description="Post operational alerts about admin account actions to your team's Slack. Applies to this stage only."
      />

      <ConnectionCard settings={settings} />

      <Stack spacing={4}>
        <Typography variant="labelMedium" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          Per-action routing
        </Typography>
        {!settings.enabled && (
          <Alert severity="info">
            Notifications are turned off above — rules below are saved but won't fire until you
            enable and configure the connection.
          </Alert>
        )}
        {settings.rules.map(rule => (
          <RuleCard key={rule.action} rule={rule} />
        ))}
      </Stack>
    </MainWrapper>
  );
}

function ConnectionCard({ settings }: { readonly settings: Settings }) {
  const [save, { loading: saving }] = useFlashMutation(adminSlackConfigUpdateMutation, {
    successMsg: 'Slack connection saved.',
    refetchQueries: ['AdminSlackSettings']
  });
  const [test, { loading: testing }] = useMutation(adminSlackTestMutation);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string | null } | null>(null);

  // Builds the mutation input from form values. botToken is write-only: '' means
  // "leave the stored token unchanged" (the field never shows the current one).
  const toInput = (values: {
    readonly enabled: boolean;
    readonly workspaceName: string;
    readonly defaultChannel: string;
    readonly botToken: string;
  }) => ({
    enabled: values.enabled,
    workspaceName: values.workspaceName.trim() === '' ? null : values.workspaceName.trim(),
    defaultChannel: values.defaultChannel.trim() === '' ? null : values.defaultChannel.trim(),
    botToken: values.botToken === '' ? null : values.botToken
  });

  const formik = useFormik({
    initialValues: {
      enabled: settings.enabled,
      workspaceName: settings.workspaceName ?? '',
      defaultChannel: settings.defaultChannel ?? '',
      botToken: ''
    },
    // No enableReinitialize: a background refetch (e.g. from saving a rule
    // below) must not wipe values the user is editing here.
    onSubmit: async values => {
      await save({ variables: { input: toInput(values) } });
      formik.setFieldValue('botToken', '');
    }
  });

  // The enable switch is authoritative — persist immediately (with the current
  // form values) so the card always reflects what's saved, matching the
  // per-action toggles below.
  const onToggleEnabled = async (next: boolean) => {
    formik.setFieldValue('enabled', next);
    await save({ variables: { input: toInput({ ...formik.values, enabled: next }) } });
  };

  const onTest = async () => {
    setTestResult(null);
    const response = await test();
    const result = response.data?.adminSlackTest ?? null;
    setTestResult(result);
  };

  return (
    <Paper variant="outlined" sx={stateCardSx(formik.values.enabled)}>
      <Stack spacing={5}>
        <Typography variant="labelMedium" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          Connection
        </Typography>

        <FormControlLabel
          // In a vertical Stack the label would stretch full-width and the whole
          // row (the <label>) becomes clickable — constrain it to its content.
          sx={{ alignSelf: 'flex-start', mr: 0 }}
          control={
            <Switch
              checked={formik.values.enabled}
              disabled={saving}
              onChange={event => onToggleEnabled(event.target.checked)}
              sx={switchOnSx}
            />
          }
          label={
            <Typography
              sx={{
                fontWeight: 600,
                color: formik.values.enabled ? ON_ACCENT : OFF_ACCENT
              }}
            >
              Enable Slack notifications
            </Typography>
          }
        />

        <Stack spacing={2}>
          <Typography variant="labelSmall" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Workspace name
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="workspaceName"
            placeholder="e.g. aSign HQ (optional, for your reference)"
            value={formik.values.workspaceName}
            onChange={formik.handleChange}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="labelSmall" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Default channel
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="defaultChannel"
            placeholder="#asign-admin or C0123ABCD"
            value={formik.values.defaultChannel}
            onChange={formik.handleChange}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="labelSmall" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Bot token
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="password"
            name="botToken"
            autoComplete="off"
            placeholder={
              settings.hasBotToken
                ? '•••••••• configured — leave blank to keep, or paste a new token to replace'
                : 'xoxb-… (Slack bot user OAuth token)'
            }
            value={formik.values.botToken}
            onChange={formik.handleChange}
          />
          <Typography
            variant="labelXSmall"
            sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
          >
            Stored in Secrets Manager, never in the database. The bot needs `chat:write` (channel
            posts) and `users:read.email` (DM by email).
          </Typography>
        </Stack>

        {testResult != null && (
          <Alert severity={testResult.ok ? 'success' : 'error'}>
            {testResult.ok ? 'Test message sent.' : `Test failed: ${testResult.error ?? 'unknown'}`}
          </Alert>
        )}

        <Stack direction="row" spacing={3}>
          <Button
            variant="primary"
            disabled={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            {formik.isSubmitting ? 'Saving…' : 'Save connection'}
          </Button>
          <Button variant="secondary" disabled={testing || !settings.hasBotToken} onClick={onTest}>
            {testing ? 'Sending…' : 'Send test'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// On/off accent colours — teal (badge.tsx `positive`) for enabled, red
// (badge.tsx `negative`) for disabled — so a card's border, tint, toggle and
// label all read as one state at a glance. Shared by the connection card and
// every per-action rule card.
const ON_ACCENT = '#1ABC9C';
const OFF_ACCENT = '#DA5B5B';

// Left accent stripe + faint matching tint, coloured by enabled state.
function stateCardSx(enabled: boolean): SxProps<Theme> {
  const accent = enabled ? ON_ACCENT : OFF_ACCENT;
  return {
    p: 6,
    borderRadius: 2,
    borderLeft: `4px solid ${accent}`,
    backgroundColor: alpha(accent, 0.06),
    transition: 'background-color 150ms ease, border-color 150ms ease'
  };
}

// Tints the switch teal when checked (the unchecked state keeps the default).
const switchOnSx: SxProps<Theme> = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: ON_ACCENT },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ON_ACCENT }
};

function RuleCard({ rule }: { readonly rule: Rule }) {
  const meta = ACTION_META[rule.action] ?? { label: rule.action, description: '' };
  const [save, { loading }] = useFlashMutation(adminSlackRuleUpdateMutation, {
    successMsg: `Saved routing for "${meta.label}".`,
    // The mutation returns the rule (no id), so it can't normalise into the
    // settings query — refetch so the cache reflects the saved truth on the
    // next render / navigation.
    refetchQueries: ['AdminSlackSettings']
  });

  const [enabled, setEnabled] = useState<boolean>(rule.enabled);
  const [channels, setChannels] = useState<readonly string[]>(rule.channels);
  const [channelDraft, setChannelDraft] = useState('');
  const [recipients, setRecipients] = useState<readonly string[]>(rule.recipients);
  const [recipientDraft, setRecipientDraft] = useState('');
  // Collapsed by default — the page reads as a compact list of action rows
  // (name + on/off); expand to edit channels/recipients.
  const [open, setOpen] = useState(false);

  // The on/off switch is authoritative — it persists immediately so the card's
  // state always reflects what's saved (no "looks on but isn't" after a
  // refresh). The chip lists keep an explicit Save. `nextEnabled` is passed in
  // because React state updates are async and we need the just-toggled value.
  const persist = async (
    nextEnabled: boolean,
    nextChannels: string[],
    nextRecipients: string[]
  ) => {
    await save({
      variables: {
        input: {
          action: rule.action,
          enabled: nextEnabled,
          channels: nextChannels,
          recipients: nextRecipients
        }
      }
    });
  };

  const onToggle = async (next: boolean) => {
    setEnabled(next);
    // Persist enabled alongside the chips currently shown (committed lists),
    // ignoring any half-typed draft text in the inputs.
    await persist(next, [...channels], [...recipients]);
  };

  const onSave = async () => {
    // Fold any uncommitted text in the chip inputs into the saved lists, so the
    // user doesn't have to commit chips separately before saving.
    const finalChannels = appendDraft(channels, channelDraft);
    const finalRecipients = appendDraft(recipients, recipientDraft);
    setChannels(finalChannels);
    setChannelDraft('');
    setRecipients(finalRecipients);
    setRecipientDraft('');
    await persist(enabled, finalChannels, finalRecipients);
  };

  return (
    <Paper variant="outlined" sx={{ ...stateCardSx(enabled), p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={3}
        onClick={() => setOpen(prev => !prev)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          // Padding lives on the header (not the Paper) so the whole top area —
          // including the vertical padding — is part of the click target.
          px: 6,
          py: 5,
          '&:hover': {
            backgroundColor: theme => alpha(theme.brandPalette.foreground.neutral, 0.03)
          }
        }}
      >
        <Typography
          variant="labelSmall"
          sx={{ fontWeight: 700, color: 'text.secondary', flex: 1, minWidth: 0 }}
        >
          {meta.label}
        </Typography>
        <FormControlLabel
          sx={{ mr: 0 }}
          // Toggling on/off must not collapse the card.
          onClick={event => event.stopPropagation()}
          control={
            <Switch
              checked={enabled}
              disabled={loading}
              onChange={event => onToggle(event.target.checked)}
              sx={switchOnSx}
            />
          }
          label={
            <Typography sx={{ fontWeight: 600, color: enabled ? ON_ACCENT : OFF_ACCENT }}>
              {enabled ? 'On' : 'Off'}
            </Typography>
          }
        />
        <IconButton
          size="small"
          // Stop the header's onClick from also firing (which would cancel this).
          onClick={event => {
            event.stopPropagation();
            setOpen(prev => !prev);
          }}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
        >
          <ExpandMoreIcon
            sx={{
              color: theme => theme.brandPalette.foreground.neutralFaded,
              transition: 'transform 180ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </IconButton>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack spacing={4} sx={{ px: 6, pb: 6 }}>
          <Typography
            variant="labelXSmall"
            sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
          >
            {meta.description}
          </Typography>

          <Divider />

          <ChipListField
            label="Channels"
            value={channels}
            onChange={setChannels}
            draft={channelDraft}
            onDraftChange={setChannelDraft}
            placeholder="#asign-admin or C0123ABCD"
            helperText="Posted to directly via chat.postMessage. Press Enter or Save to add."
          />

          <ChipListField
            label="Direct-message recipients"
            value={recipients}
            onChange={setRecipients}
            draft={recipientDraft}
            onDraftChange={setRecipientDraft}
            placeholder="@handle, U0123ABCD, or email"
            helperText="Slack @handle, member id, or email (resolved via users.lookupByEmail). Not tied to aSign user emails."
          />

          <Stack direction="row">
            <Button variant="primary" disabled={loading} onClick={onSave}>
              {loading ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Paper>
  );
}

// Appends trimmed draft text to a list unless empty or a case-insensitive
// duplicate. Used so a Save persists whatever is typed in a chip input.
function appendDraft(list: readonly string[], draft: string): string[] {
  const entry = draft.trim();
  if (entry === '' || list.some(item => item.toLowerCase() === entry.toLowerCase())) {
    return [...list];
  }
  return [...list, entry];
}

const adminSlackSettingsQuery = gql<AdminSlackSettingsGQL>`
  query AdminSlackSettings {
    adminSlackSettings {
      enabled
      defaultChannel
      workspaceName
      hasBotToken
      rules {
        action
        enabled
        channels
        recipients
      }
    }
  }
`;

const adminSlackConfigUpdateMutation = gql<AdminSlackConfigUpdateGQL>`
  mutation AdminSlackConfigUpdate($input: AdminSlackConfigUpdateInput!) {
    adminSlackConfigUpdate(input: $input) {
      enabled
      defaultChannel
      workspaceName
      hasBotToken
      rules {
        action
        enabled
        channels
        recipients
      }
    }
  }
`;

const adminSlackRuleUpdateMutation = gql<AdminSlackRuleUpdateGQL>`
  mutation AdminSlackRuleUpdate($input: AdminSlackRuleUpdateInput!) {
    adminSlackRuleUpdate(input: $input) {
      action
      enabled
      channels
      recipients
    }
  }
`;

const adminSlackTestMutation = gql<AdminSlackTestGQL>`
  mutation AdminSlackTest($input: AdminSlackTestInput) {
    adminSlackTest(input: $input) {
      ok
      error
    }
  }
`;
